import { z } from 'zod';

/**
 * Shared primitives. These match the platform's ID/format conventions so data stays
 * portable and merge/sync-safe. See docs/DATA-MODEL.md and AGENTS.md.
 */

// Entity ids are TEXT UUIDs (crypto.randomUUID). The platform historically allows mixed
// id formats, so we validate "non-empty string" rather than strict uuid.
export const id = z.string().min(1);

// Organization ids come from the platform in `org_xxx` form — not a UUID.
export const organizationId = z.string().min(1);

// Timestamps are ISO 8601 strings (SQLite datetime('now')).
export const timestamp = z.string().min(1);

// Money is stored as integer cents. Never floats-as-currency, never strings.
export const cents = z.number().int().nonnegative();

/** Fields every persisted dental entity carries. */
export const entityBase = z.object({
  id,
  organizationId,
  createdAt: timestamp,
  updatedAt: timestamp,
});

export type Id = z.infer<typeof id>;
export type OrganizationId = z.infer<typeof organizationId>;
export type Timestamp = z.infer<typeof timestamp>;

/** Standard API envelope returned by the dental API. */
export type ApiResult<T> =
  { success: true; data: T } | { success: false; error: string; code?: string };

/** Consistent query contract for every growing collection endpoint. */
export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type PaginationQuery = z.infer<typeof paginationQuery>;

/** Stable metadata returned with a server-paginated collection. */
export interface PaginatedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Builds a runtime schema for paginated boundary validation when a consumer needs it. */
export function paginatedResult<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(100),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  });
}
